import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Clock, FlaskConical, Home, LogOut, Search, User } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useAuth } from "@/lib/auth";
import { getNavStructure, flattenPrimary } from "@/lib/nav";
import { getRecentVisits } from "@/lib/recentVisits";
import api from "@/lib/api";

// Detect whether the platform shortcut should be ⌘K (macOS) or Ctrl+K.
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/.test(navigator.platform || "");

// Icon + label tuple keyed by /api/admin/search result kind.
const KIND_META = {
  customer:  { Icon: User,          label: "Customer" },
  booking:   { Icon: Briefcase,     label: "Booking" },
  lab_test:  { Icon: FlaskConical,  label: "Lab test" },
  property:  { Icon: Home,          label: "Property" },
};

/**
 * Global ⌘K / Ctrl+K command palette.
 *
 * Two modes:
 *   • Static — lists every nav link for the current role + Log out.
 *   • Dynamic — admins only. When the query is >=2 chars, fetches matching
 *     customers / bookings / lab tests / properties from /api/admin/search
 *     (debounced 250ms) and surfaces them in a "Jump to…" group above the
 *     nav links so admins can jump directly to a record by typing.
 */
export function CommandPalette() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dynamicResults, setDynamicResults] = useState([]);
  const [dynamicLoading, setDynamicLoading] = useState(false);
  // Default-state panels (only loaded when the palette opens with no query)
  const [recentVisits, setRecentVisits] = useState([]);
  const [awaitingReview, setAwaitingReview] = useState([]);

  const rawNav = user ? getNavStructure(user.role) : { primary: [], settings: [] };
  const nav = {
    primary: flattenPrimary(rawNav.primary),
    settings: (rawNav.settings || []).filter((l) => l && l.to),
  };
  const total = nav.primary.length + nav.settings.length;
  const isAdmin = user?.role === "admin";
  const trimmedQuery = query.trim();

  // Reset state every time the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) { setQuery(""); setDynamicResults([]); setDynamicLoading(false); }
  }, [open]);

  // On open: load default panels (recent visits from localStorage,
  // awaiting-review from API). Cheap — only fires once per open.
  useEffect(() => {
    if (!open) return;
    setRecentVisits(getRecentVisits(8));
    if (isAdmin) {
      api.get("/admin/search/awaiting-review")
        .then(({ data }) => setAwaitingReview(data?.results || []))
        .catch(() => setAwaitingReview([]));
    } else {
      setAwaitingReview([]);
    }
  }, [open, isAdmin]);

  // Debounced dynamic search (admins only, queries >=2 chars).
  useEffect(() => {
    if (!isAdmin || !open) return;
    const q = trimmedQuery;
    if (q.length < 2) { setDynamicResults([]); setDynamicLoading(false); return; }
    let cancelled = false;
    setDynamicLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setDynamicResults(data?.results || []);
      } catch (_e) {
        if (!cancelled) setDynamicResults([]);
      } finally {
        if (!cancelled) setDynamicLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [trimmedQuery, open, isAdmin]);

  // Global keyboard shortcut. Only registered when there's something to
  // navigate to (i.e. an authenticated user with non-empty nav).
  useEffect(() => {
    if (!user || total === 0) return;
    const onKey = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, total]);

  if (!user || total === 0) return null;

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <>
      <button
        data-testid="command-palette-trigger"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open command palette"
        className="hidden lg:inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-xs text-slate-500 transition-colors flex-shrink-0"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="hidden xl:inline">Search…</span>
        <kbd className="hidden xl:inline-block px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono text-[10px] text-slate-500">
          {IS_MAC ? "⌘K" : "Ctrl K"}
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          data-testid="command-palette-input"
          placeholder={isAdmin
            ? "Search pages, customers, bookings, samples…"
            : "Search admin pages…"}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList data-testid="command-palette-list">
          <CommandEmpty>
            {dynamicLoading ? "Searching…" : "No matching results."}
          </CommandEmpty>

          {/* Default-state panels (admins only, only when no query typed).
              Surfaces Recently viewed + Awaiting your review above static
              nav so admins can jump straight back into yesterday's work. */}
          {isAdmin && trimmedQuery.length === 0 && awaitingReview.length > 0 && (
            <>
              <CommandGroup heading="Awaiting your review">
                {awaitingReview.map((r) => (
                  <CommandItem
                    key={`awaiting:${r.id}`}
                    value={`awaiting ${r.id} ${r.label} ${r.sublabel || ""}`}
                    onSelect={() => go(r.to)}
                    data-testid={`command-palette-awaiting-${r.id}`}
                  >
                    <FlaskConical className="w-4 h-4 mr-2 text-amber-500 flex-shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{r.label}</span>
                      {r.sublabel && <span className="text-[11px] text-slate-500 truncate">{r.sublabel}</span>}
                    </div>
                    <CommandShortcut>Review</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {isAdmin && trimmedQuery.length === 0 && recentVisits.length > 0 && (
            <>
              <CommandGroup heading="Recently viewed">
                {recentVisits.map((r) => {
                  const meta = KIND_META[r.kind] || { Icon: Search, label: r.kind };
                  const Icon = meta.Icon;
                  return (
                    <CommandItem
                      key={`recent:${r.kind}:${r.id}`}
                      value={`recent ${r.kind} ${r.id} ${r.label} ${r.sublabel || ""}`}
                      onSelect={() => go(r.to)}
                      data-testid={`command-palette-recent-${r.kind}-${r.id}`}
                    >
                      <Icon className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{r.label}</span>
                        {r.sublabel && <span className="text-[11px] text-slate-500 truncate">{r.sublabel}</span>}
                      </div>
                      <CommandShortcut className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{meta.label}</CommandShortcut>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Dynamic results — admins only, surface above the static nav. */}
          {isAdmin && dynamicResults.length > 0 && (
            <>
              <CommandGroup heading="Jump to…">
                {dynamicResults.map((r) => {
                  const meta = KIND_META[r.kind] || { Icon: Search, label: r.kind };
                  const Icon = meta.Icon;
                  return (
                    <CommandItem
                      key={`${r.kind}:${r.id}`}
                      value={`${r.kind}:${r.id} ${r.label} ${r.sublabel || ""}`}
                      onSelect={() => go(r.to)}
                      data-testid={`command-palette-dynamic-${r.kind}-${r.id}`}
                    >
                      <Icon className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{r.label}</span>
                        {r.sublabel && <span className="text-[11px] text-slate-500 truncate">{r.sublabel}</span>}
                      </div>
                      <CommandShortcut>{meta.label}</CommandShortcut>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {nav.primary.length > 0 && (
            <CommandGroup heading="Primary">
              {nav.primary.map((l) => (
                <CommandItem
                  key={l.to}
                  value={l.label}
                  onSelect={() => go(l.to)}
                  data-testid={`command-palette-item-${l.to.replace(/\//g, "-")}`}
                >
                  <span>{l.label}</span>
                  <CommandShortcut>{l.to}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {nav.settings.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Settings">
                {nav.settings.map((l) => (
                  <CommandItem
                    key={l.to}
                    value={l.label}
                    onSelect={() => go(l.to)}
                    data-testid={`command-palette-item-${l.to.replace(/\//g, "-")}`}
                  >
                    <span>{l.label}</span>
                    <CommandShortcut>{l.to}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />
          <CommandGroup heading="Account">
            <CommandItem
              value="Log out"
              onSelect={async () => { setOpen(false); await logout(); window.location.assign("/"); }}
              data-testid="command-palette-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span>Log out</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
